import { hasPositionViewport, type AssistantPosition } from './assistantPosition';
import { readSavedBasketTips } from './personalization';
import { create } from 'zustand';
import { persist, subscribeWithSelector, createJSONStorage } from 'zustand/middleware';
import { ShoppingItem, CategoryInfo, UserProfile, StoreInfo, ShoppingHistoryEntry, BasketTip, InsightData } from '../../types';
import { DEFAULT_CATEGORIES, LANGUAGES } from '../../constants';
import i18n from '../i18n/config';
import { 
  getProfile, 
  getHistory, 
  getCategories, 
  createProfile,
  getMasterInsight,
  getBasketInsight,
  updateProfile,
  spendCredits as spendCreditsRpc
} from '../../services/supabaseService';
import { slugify, constructProtocolSlug, formatDisplayName, destructureName } from './intelligence';
import { buildBasketItem } from './basketItem';
import { fetchSpendingAnalytics } from './cachedQueries';
import { CONSENT_VERSION, CookieConsent, UNDECIDED_CONSENT, fromConsentSetting, toConsentSetting } from './consent';

// Keeps the URL's `?lng=` in sync with an in-app language switch — without this,
// SEO tags (which derive from the URL on refresh/SSR/crawl) never reflect the choice.
const syncUrlLanguage = (language: string) => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  // Matches SEO.tsx's canonical-URL convention: English is the paramless default.
  if (language === 'en') {
    url.searchParams.delete('lng');
  } else {
    url.searchParams.set('lng', language);
  }
  window.history.replaceState(window.history.state, '', url);
};

interface AppState {
  // Items / Basket
  items: ShoppingItem[];
  latticeItems: ShoppingItem[];
  operatingContext: 'personal' | 'personnel';
  setOperatingContext: (context: 'personal' | 'personnel') => void;
  itemsMetadata: Record<string, any>;
  setItems: (items: ShoppingItem[]) => void;
  addItem: (
    name: string, 
    price: number, 
    category: string, 
    imageUrl?: string, 
    quantity?: number, 
    canonicalId?: string, 
    unit?: string,
    brand?: string | null,
    product?: string | null,
    variety?: string | null,
    size?: number | string | null,
    shelfLife?: number | null,
    embedding?: number[] | null,
    plu_code?: string | null,
    metadata?: any,
    upc?: string | null,
    gtin?: string | null
  ) => string | undefined; // the new item's id
  removeItem: (id: string) => void;
  updateItem: (id: string, updates: Partial<ShoppingItem>) => void;
  clearBasket: () => void;

  // Categories
  categories: CategoryInfo[];
  setCategories: (categories: CategoryInfo[] | ((prev: CategoryInfo[]) => CategoryInfo[])) => void;
  lastCategoriesUpdate: number;

  // User / Profile
  userProfile: UserProfile | null;
  setUserProfile: (profile: UserProfile | null) => void;
  isProUser: boolean;
  setIsProUser: (isPro: boolean) => void;
  isUltraUser: boolean;
  setIsUltraUser: (isUltra: boolean) => void;
  isEligibleForFreeTrial: boolean;
  setIsEligibleForFreeTrial: (eligible: boolean) => void;
  subscriptionInterval: 'month' | 'year';
  setSubscriptionInterval: (interval: 'month' | 'year') => void;
  subscriptionEndsAt: string | null;
  setSubscriptionEndsAt: (date: string | null) => void;
  trialEndsAt: string | null;
  setTrialEndsAt: (date: string | null) => void;
  cancelAtPeriodEnd: boolean;
  setCancelAtPeriodEnd: (cancelled: boolean) => void;
  /** The account the persisted plan belongs to, so another user on this device never sees it. */
  tierOwnerId: string | null;
  /** True once this session has the server's answer (applyEntitlement); older sources then stop writing the plan. */
  entitlementFromServer: boolean;
  /** The one way the server's plan answer (subscription-status, reconcile, or a billing action's `entitlement`) reaches the app. */
  applyEntitlement: (entitlement: Entitlement | null | undefined, userId?: string | null) => void;
  /** Back to Free, e.g. when a different account signs in on this device. */
  resetEntitlement: () => void;

  // Merchant / Context
  currentStore: StoreInfo | null;
  setCurrentStore: (store: StoreInfo | null) => void;
  selectStore: (store: StoreInfo | null) => void;
  tripDateTime: Date;
  setTripDateTime: (date: Date) => void;
  // Trip Budget (PERSONAL_NOTES.md#trip-budget): this store visit's budget, or
  // null. Remaining is derived from the basket total, never stored.
  tripBudget: number | null;
  setTripBudget: (amount: number | null) => void;
  // The user closed the budget sheet without a budget: don't ask again this trip.
  budgetPromptDismissed: boolean;
  setBudgetPromptDismissed: (dismissed: boolean) => void;
  /** Ends the trip's budget (after archiving): no budget, and ask again next trip. */
  resetTripBudget: () => void;
  isSyncing: boolean;
  setIsSyncing: (syncing: boolean) => void;
  isTimeManual: boolean;
  setIsTimeManual: (isManual: boolean) => void;
  receiptImageUrl: string | null;
  setReceiptImageUrl: (url: string | null) => void;
  receiptSubtotal: number | null;
  setReceiptSubtotal: (subtotal: number | null) => void;
  unitSystem: 'imperial' | 'metric';
  setUnitSystem: (system: 'imperial' | 'metric') => void;
  massUnits: string[];
  setMassUnits: (units: string[]) => void;
  zipCode: string | null;
  setZipCode: (zip: string | null) => void;
  language: string;
  setLanguage: (lang: string) => void;
  lastLanguageChangeAt: number;
  currency: string;
  setCurrency: (currency: string) => void;

  // History
  history: ShoppingHistoryEntry[];
  setHistory: (history: ShoppingHistoryEntry[] | ((prev: ShoppingHistoryEntry[]) => ShoppingHistoryEntry[])) => void;
  lastHistoryUpdate: number;
  lastSettingsUpdate: number;
  lastTripSavedTimestamp: number;
  // Renamed from latticeArchitect (2026-07-21): avoids a naming collision with
  // the unrelated is_architect/architect_applications role system, and moved
  // to settings.privacy.lattice_participation (was profiles.metadata.lattice_architect)
  // per CLAUDE.md's convention that user preferences live in `settings`, not `metadata`.
  latticeParticipation: boolean;
  setLatticeParticipation: (enabled: boolean) => void;

  // Insights & Intelligence
  analytics: any | null;
  setAnalytics: (analytics: any) => void;
  dismissedSuggestions: string[];
  dismissSuggestion: (name: string) => void;
  basketInsight: BasketTip[] | null;
  setBasketInsight: (insight: BasketTip[] | null, profileSignature?: string) => void;
  basketInsightProfileSignature: string | null;
  lastInsightGeneratedTimestamp: number;
  setLastInsightGeneratedTimestamp: (timestamp: number) => void;
  lastBasketChangeTimestamp: number;
  setLastBasketChangeTimestamp: (timestamp: number) => void;
  masterInsight: InsightData | null;
  setMasterInsight: (insight: InsightData | null) => void;
  isInsightOnCooldown: boolean;
  cooldownTimeLeft: number;
  handleBypassCooldown: (type: 'audit' | 'tips') => void;

  // Merchant & Geospatial
  nearbyStores: StoreInfo[];
  setNearbyStores: (stores: StoreInfo[]) => void;

  // Assistant & UI
  chatPosition: AssistantPosition | null;
  setChatPosition: (pos: AssistantPosition | null) => void;
  // Cookie consent (see src/lib/consent.ts). Signed-in users also keep it in
  // profiles.settings.privacy.cookie_consent.
  cookieConsent: CookieConsent;
  setCookieConsent: (choice: { analytics: boolean; advertising: boolean }) => void;
  isConsentPanelOpen: boolean;
  setConsentPanelOpen: (open: boolean) => void;
  /** This visitor's region needs an opt-in for Analytics (GET /api/consent-policy). Not persisted. */
  consentOptInRequired: boolean;
  setConsentOptInRequired: (required: boolean) => void;
  hasCompletedTour: boolean;
  setHasCompletedTour: (completed: boolean) => void;
  completedTours: string[]; // List of page IDs where tour is finished
  setTourCompleted: (tourId: string) => void;
  resetTours: () => void;

  // Guardrails & Confirmations
  confirmations: Record<string, { status: 'confirmed' | 'cancelled'; threadId?: string; timestamp: number }>;
  setConfirmation: (nonce: string, status: 'confirmed' | 'cancelled', threadId?: string) => void;
  clearConfirmations: (threadId?: string) => void;

  // Hydration & Loading
  hydrated: boolean;
  setHydrated: (val: boolean) => void;
  isCinematicLoading: boolean;
  appLoadingProgress: number;
  load: (supabase: any, userId: string, force?: boolean, clerkUser?: any) => Promise<void>;
  saveItemImage: (id: string, imageUrl: string) => void;
  syncLocalSettings: () => Promise<void>;

  // PWA Status
  pwaInstalled: boolean;
  setPwaInstalled: (installed: boolean) => void;
  pwaInstallDismissed: number | null; // Timestamp of last dismissal
  setPwaInstallDismissed: (timestamp: number | null) => void;

  // Cloud Sync & Credits
  /** Profile edits a screen hasn't saved yet; the exit flush includes them (see buildCloudSyncPayload). */
  pendingProfilePatch: { ai_profile?: any; settings?: any } | null;
  setPendingProfilePatch: (patch: { ai_profile?: any; settings?: any } | null) => void;
  /** Records a patch that was sent to the server into the loaded profile copy. */
  markProfilePatched: (patch: any) => void;
  /** Re-reads plan, credits and storage; leaves the user-editable profile parts alone. */
  refreshAccount: (supabase: import('@supabase/supabase-js').SupabaseClient, userId: string) => Promise<void>;
  syncStateToCloud: (supabase: import('@supabase/supabase-js').SupabaseClient, userId: string) => Promise<void>;
  spendCredits: (amount?: number) => Promise<boolean>;

  // Optimistic UI / Processing
  pendingItems: Array<{ id: string; imageUrl?: string; mode: 'receipt' | 'tag' }>;
  addPendingItem: (id: string, mode: 'receipt' | 'tag', imageUrl?: string) => void;
  removePendingItem: (id: string) => void;

  // Bulk Import: batch ids the user has already opened BulkImportView for since
  // that batch reached its current state — suppresses the topbar completion
  // toast and clears the icon dot once the user has actually looked.
  seenBulkImportBatchIds: string[];
  markBulkImportBatchSeen: (batchId: string) => void;

  // Visual Events / UI Bridge
  triggerAction: (action: string, data?: any) => void;
  registerAction: (action: string, callback: (data?: any) => void) => void;
  unregisterAction: (action: string) => void;
  uiActions: Record<string, (data?: any) => void>;
}

export const liveState = {
  items: [] as any[],
  categories: [] as any[],
  history: [] as any[],
  profile: null as any,
  triggerAction: (action: string, data?: any) => {},
  receiptMetadataSource: null as { storeName?: string; storeAddress?: string } | null,
  applyReceiptMetadata: null as ((result: { 
    storeName?: string; 
    storeAddress?: string; 
    date?: string; 
    time?: string;
    subtotal?: number;
    totalAmount?: number;
    lat?: number;
    lng?: number;
    skipStoreResolution?: boolean;
  }) => Promise<void>) | null,
  saveToHistory: null as ((isLatticeContribution?: boolean) => Promise<void>) | null,
  triggerArchiveModal: null as (() => void) | null,
  pendingReceiptItems: [] as any[],
  pendingExtractedItems: [] as any[],
  lastMarketDiscoveryProducts: [] as any[],
  lastThreadImageRef: null as { url: string; filename: string; timestamp: number; threadId: string } | null,
  // Placeholders — replaced below after useStore is created
  setCurrentStore: (_store: StoreInfo | null) => {},
  setTripDateTime: (_date: Date) => {},
  setIsTimeManual: (_v: boolean) => {},
  setReceiptImageUrl: (_url: string | null) => {},
  setReceiptSubtotal: (_subtotal: number | null) => {},
};

/** The saved ZIP: settings.location.zip_code, or the legacy profiles.zip_code column. */
/** The server's answer about the account's plan; see getEntitlementStatus in api/index.js. */
export interface Entitlement {
  isPro: boolean;
  isUltra: boolean;
  interval?: 'month' | 'year';
  subscriptionEndsAt?: string | null;
  trialEndsAt?: string | null;
  cancelAtPeriodEnd?: boolean;
  isEligibleForFreeTrial?: boolean;
  syncing?: boolean;
  pendingChange?: any;
  nextPayment?: any;
}

/**
 * Plan fields from a Supabase profile, used only until the server's own answer
 * arrives this session: the profile can trail Clerk Billing by a webhook, and
 * letting it overwrite a fresher answer made the plan flicker.
 */
const tierFromProfile = (state: { entitlementFromServer: boolean }, profile: UserProfile): Partial<AppState> => {
  if (state.entitlementFromServer) return {};
  const updates: Partial<AppState> = {
    isProUser: !!profile.is_pro,
    isUltraUser: !!profile.is_ultra,
    tierOwnerId: profile.id,
  };
  if (profile.subscription_interval) updates.subscriptionInterval = profile.subscription_interval as 'month' | 'year';
  if (profile.subscription_ends_at) updates.subscriptionEndsAt = profile.subscription_ends_at;
  if (profile.trial_ends_at) updates.trialEndsAt = profile.trial_ends_at;
  if (profile.cancel_at_period_end !== undefined) updates.cancelAtPeriodEnd = !!profile.cancel_at_period_end;
  return updates;
};

export const profileZipCode = (profile: UserProfile | null | undefined): string | null =>
  (profile?.settings as any)?.location?.zip_code ?? profile?.zip_code ?? null;

/** Leaves of `next` that differ from `current` (deep), or undefined when nothing changed. */
const changedLeaves = (next: any, current: any): any => {
  if (next && typeof next === 'object' && !Array.isArray(next)) {
    const out: Record<string, any> = {};
    for (const key of Object.keys(next)) {
      const diff = changedLeaves(next[key], current?.[key]);
      if (diff !== undefined) out[key] = diff;
    }
    return Object.keys(out).length ? out : undefined;
  }
  return JSON.stringify(next ?? null) === JSON.stringify(current ?? null) ? undefined : next;
};

/** Deep-merges a patch into a profile the way patch_profile does (objects merge, everything else replaces). */
export const mergeProfilePatch = (base: any, patch: any): any => {
  if (patch === undefined) return base;
  if (!patch || typeof patch !== 'object' || Array.isArray(patch)) return patch;
  const out: Record<string, any> = { ...(base && typeof base === 'object' && !Array.isArray(base) ? base : {}) };
  for (const [key, value] of Object.entries(patch)) out[key] = mergeProfilePatch(out[key], value);
  return out;
};

/**
 * The profile patch for the background sync and the exit flush (/api/sync/flush):
 * device state mirrored into the profile, plus edits a screen hasn't saved yet
 * (pendingProfilePatch, e.g. Settings during its debounce). Only fields that
 * differ from the loaded profile are included, so an unchanged session writes
 * nothing, and nothing is sent before the profile has loaded, so a sync can
 * never write defaults or a null ai_profile over the user's real settings.
 */
export const buildCloudSyncPayload = (state: AppState) => {
  const profile = state.userProfile;
  if (!profile) return null;
  const consent = toConsentSetting(state.cookieConsent);
  const pending = state.pendingProfilePatch || {};
  const full = {
    metadata: {
      chatPosition: state.chatPosition,
      unitSystem: state.unitSystem,
      massUnits: state.massUnits,
      currentStore: state.currentStore,
      tripBudget: state.tripBudget,
      budgetPromptDismissed: state.budgetPromptDismissed,
      isTimeManual: state.isTimeManual,
      confirmations: state.confirmations,
      hasCompletedTour: state.hasCompletedTour,
      completedTours: state.completedTours,
      lastBasketChangeTimestamp: state.lastBasketChangeTimestamp,
      lastInsightGeneratedTimestamp: state.lastInsightGeneratedTimestamp,
      nearbyStores: state.nearbyStores,
    },
    settings: mergeProfilePatch({
      interface: { language: state.language, currency: state.currency },
      privacy: {
        lattice_participation: state.latticeParticipation,
        ...(consent ? { cookie_consent: consent } : {}),
      },
      location: { zip_code: state.zipCode || null },
    }, pending.settings),
    ...(pending.ai_profile && typeof pending.ai_profile === 'object' ? { ai_profile: pending.ai_profile } : {}),
  };
  const current = { metadata: profile.metadata, settings: profile.settings, ai_profile: profile.ai_profile };
  return changedLeaves(full, current) || null;
};

export const useStore = create<AppState>()(
  subscribeWithSelector(
    persist(
      (set, get) => ({
        // Optimistic UI
        pendingItems: [],
        addPendingItem: (id, mode, imageUrl) => set((state) => {
          // HAPTIC: Subtle pulse when processing starts
          if (typeof navigator !== 'undefined' && navigator.vibrate) {
            navigator.vibrate(10);
          }
          return { pendingItems: [...state.pendingItems, { id, mode, imageUrl }] };
        }),
        removePendingItem: (id) => set((state) => ({
          pendingItems: state.pendingItems.filter(p => p.id !== id)
        })),

        seenBulkImportBatchIds: [],
        markBulkImportBatchSeen: (batchId) => set((state) => (
          state.seenBulkImportBatchIds.includes(batchId)
            ? state
            : { seenBulkImportBatchIds: [...state.seenBulkImportBatchIds, batchId].slice(-20) }
        )),

        // Items
        items: [],
        latticeItems: [],
        operatingContext: 'personal',
        setOperatingContext: (operatingContext) => set({ operatingContext }),
        itemsMetadata: {},
        setItems: (newItems) => {
          const context = get().operatingContext;
          if (context === 'personnel') {
            set({ 
              latticeItems: newItems,
              lastSettingsUpdate: Date.now() 
            });
          } else {
            set({ 
              items: newItems, 
              lastBasketChangeTimestamp: newItems.length > 0 ? (get().lastBasketChangeTimestamp || Date.now()) : 0, 
              lastSettingsUpdate: Date.now() 
            });
          }
        },
        addItem: (name, price, category, imageUrl, quantity = 1, canonicalId, unit, brand, product, variety, size, shelfLife, embedding, plu_code, metadata, upc, gtin) => {
          // HAPTIC: Confirm success pulse
          if (typeof navigator !== 'undefined' && navigator.vibrate) {
            navigator.vibrate([15, 30, 15]);
          }
          let newId: string | undefined;
          try {
            set((state) => {
              const newItem = buildBasketItem(
                { name, price, category, imageUrl, quantity, canonicalId, unit, brand, product, variety, size, shelfLife, embedding, plu_code, metadata, upc, gtin },
                state.tripDateTime
              );
              newId = newItem.id;

              const isPersonnel = state.operatingContext === 'personnel';
              if (isPersonnel) {
                return {
                  latticeItems: [newItem, ...state.latticeItems],
                  lastSettingsUpdate: Date.now()
                };
              }

              return { 
                items: [newItem, ...state.items],
                lastBasketChangeTimestamp: Date.now(), // Real change
                lastSettingsUpdate: Date.now()
              };
            });
          } catch (e) {
            console.error("Store: addItem error:", e);
          }
          return newId;
        },
        removeItem: (id) => set((state) => {
          const isPersonnel = state.operatingContext === 'personnel';
          if (isPersonnel) {
            return {
              latticeItems: state.latticeItems.filter((i) => i.id !== id),
              lastSettingsUpdate: Date.now()
            };
          }
          return { 
            items: state.items.filter((i) => i.id !== id),
            lastBasketChangeTimestamp: Date.now(), // Real change
            lastSettingsUpdate: Date.now()
          };
        }),
        updateItem: (id, updates) => {
          try {
            set((state) => {
              const isPersonnel = state.operatingContext === 'personnel';
              const targetList = isPersonnel ? state.latticeItems : state.items;
              
              const triggersInsight = !isPersonnel && Object.keys(updates).some(k => ['name', 'price', 'quantity', 'category', 'unit', 'imageUrl', 'brand', 'product', 'variety', 'size', 'embedding', 'shelfLife'].includes(k));
              
              const updatedList = targetList.map((i) => {
                if (i.id === id) {
                  const next = { ...i, ...updates };

                  // If name is changed but structured fields are missing, de-structure it
                  if (updates.name && !updates.product && !updates.brand) {
                    const parts = destructureName(updates.name);
                    if (parts.brand) next.brand = parts.brand;
                    if (parts.product) next.product = parts.product;
                    if (parts.variety) next.variety = parts.variety;
                    if (parts.size) next.size = parts.size;
                    if (parts.unit) next.unit = parts.unit;
                  }

                  // If structural fields are present in update, rebuild name and slug to ensure global consistency
                  const hasStructuralChanges = updates.brand !== undefined || updates.product !== undefined || updates.variety !== undefined || updates.size !== undefined || updates.unit !== undefined;

                  if (hasStructuralChanges) {
                    // Rebuild core identity from the latest state of structural fields
                    next.name = formatDisplayName(next);
                    
                    // Regenerate slug only if not explicitly provided in the update object
                    if (!updates.canonicalId) {
                      next.canonicalId = constructProtocolSlug(next);
                    }
                  } else if (updates.name && updates.name !== i.name) {
                    // AUTO-DESTRUCTURE FALLBACK: If name is updated but structural fields are not
                    const parts = destructureName(updates.name);
                    next.brand = parts.brand;
                    next.product = parts.product;
                    next.variety = parts.variety;
                    next.size = parts.size;
                    next.unit = parts.unit;
                  }

                  // If price changed, clear any previous betterDeal to force a re-scan
                  if (updates.price !== undefined && updates.price !== i.price) {
                    next.betterDeal = null;
                    next.priceCheck = null;
                  }

                  // Legacy auto-slugify (fallback)
                  if (updates.name && updates.name !== i.name && !updates.canonicalId && !hasStructuralChanges) {
                    next.canonicalId = slugify(updates.name);
                  }
                  return next;
                }
                return i;
              });

              if (isPersonnel) {
                return {
                  latticeItems: updatedList,
                  lastSettingsUpdate: Date.now()
                };
              }

              return {
                lastBasketChangeTimestamp: triggersInsight ? Date.now() : state.lastBasketChangeTimestamp,
                lastSettingsUpdate: triggersInsight ? Date.now() : state.lastSettingsUpdate,
                items: updatedList
              };
            });
          } catch (e) {
            console.error("Store: updateItem error:", e);
          }
        },
        clearBasket: () => {
          try {
            // PIVOT: Clear local image cache and mapping registry
            import('../../services/imageCacheService').then(({ ImageCacheService }) => {
              ImageCacheService.clearAll();
            });

            // CRITICAL: Also clear staged items in liveState to prevent ghost persistence
            liveState.pendingExtractedItems = [];
            liveState.pendingReceiptItems = [];

            const isPersonnel = get().operatingContext === 'personnel';
            if (isPersonnel) {
              set({
                latticeItems: [],
                lastSettingsUpdate: Date.now()
              });
            } else {
              set({ 
                items: [], 
                receiptImageUrl: null, 
                receiptSubtotal: null,
                lastBasketChangeTimestamp: 0,
                lastInsightGeneratedTimestamp: 0,
                basketInsight: null,
                lastSettingsUpdate: Date.now()
              });
            }

            // ATOMIC SYNC: Ensure cloud state reflects the cleared basket immediately
            const { userProfile } = get();
            if (userProfile?.id) {
              // Note: We'd need the supabase client here if we wanted to sync. 
              // Since it's not in the store, we rely on App.tsx or other components to trigger sync if needed,
              // or we can import the singleton if available.
              // For now, we just don't try to destructure what's not there.
            }
          } catch (e) {
            console.error("Store: clearBasket error:", e);
          }
        },        saveItemImage: (id, imageUrl) => get().updateItem(id, { imageUrl }),

        // Categories
        categories: DEFAULT_CATEGORIES,
        lastCategoriesUpdate: 0,
        setCategories: (categoriesOrFn) => set((state) => ({
          categories: typeof categoriesOrFn === 'function' ? categoriesOrFn(state.categories) : categoriesOrFn,
          lastCategoriesUpdate: Date.now()
        })),

        // User
        userProfile: null,
        setUserProfile: (userProfile) => set((state) => {
          if (!userProfile) return { userProfile: null };
          
          const updates: Partial<AppState> = { 
            userProfile,
            ...tierFromProfile(state, userProfile),
            zipCode: profileZipCode(userProfile),
            lastSettingsUpdate: Date.now()
          };
          
          if (userProfile.settings?.interface?.currency) updates.currency = userProfile.settings.interface.currency;
          if (userProfile.settings?.interface?.language) updates.language = userProfile.settings.interface.language;
          if (userProfile.settings?.privacy?.lattice_participation !== undefined) {
            updates.latticeParticipation = userProfile.settings.privacy.lattice_participation;
          }

          if (userProfile.metadata) {
            const m = userProfile.metadata;
            if (!state.chatPosition && hasPositionViewport(m.chatPosition)) updates.chatPosition = m.chatPosition;
            if (m.unitSystem) updates.unitSystem = m.unitSystem;
            if (m.massUnits) updates.massUnits = m.massUnits;
            if (m.currentStore) updates.currentStore = m.currentStore;
            if (m.tripBudget !== undefined) updates.tripBudget = m.tripBudget;
            if (m.budgetPromptDismissed !== undefined) updates.budgetPromptDismissed = m.budgetPromptDismissed;
            if (m.isTimeManual !== undefined) updates.isTimeManual = m.isTimeManual;
            if (m.confirmations) updates.confirmations = m.confirmations;
          }
          {
            const remote = fromConsentSetting(userProfile.settings?.privacy?.cookie_consent);
            if (remote && (!state.cookieConsent.decidedAt || remote.decidedAt! > state.cookieConsent.decidedAt)) {
              updates.cookieConsent = remote;
            }
          }

          return updates;
        }),
        isProUser: false,
        setIsProUser: (isProUser) => set({ isProUser }),
        isUltraUser: false,
        setIsUltraUser: (isUltraUser) => set({ isUltraUser }),
        isEligibleForFreeTrial: true,
        setIsEligibleForFreeTrial: (isEligibleForFreeTrial) => set({ isEligibleForFreeTrial }),
        subscriptionInterval: 'month',
        setSubscriptionInterval: (subscriptionInterval) => set({ subscriptionInterval }),
        subscriptionEndsAt: null,
        setSubscriptionEndsAt: (subscriptionEndsAt) => set({ subscriptionEndsAt }),
        trialEndsAt: null,
        setTrialEndsAt: (trialEndsAt) => set({ trialEndsAt }),
        cancelAtPeriodEnd: false,
        tierOwnerId: null,
        entitlementFromServer: false,
        applyEntitlement: (entitlement, userId) => {
          if (!entitlement || typeof entitlement.isPro !== 'boolean') return;
          set((state) => ({
            isProUser: entitlement.isPro,
            isUltraUser: !!entitlement.isUltra,
            ...(entitlement.interval ? { subscriptionInterval: entitlement.interval } : {}),
            subscriptionEndsAt: entitlement.subscriptionEndsAt ?? null,
            trialEndsAt: entitlement.trialEndsAt ?? null,
            cancelAtPeriodEnd: !!entitlement.cancelAtPeriodEnd,
            ...(typeof entitlement.isEligibleForFreeTrial === 'boolean' ? { isEligibleForFreeTrial: entitlement.isEligibleForFreeTrial } : {}),
            tierOwnerId: userId ?? state.userProfile?.id ?? state.tierOwnerId,
            entitlementFromServer: true,
            // Keep the loaded profile's copy in step, so nothing reads an older plan from it.
            userProfile: state.userProfile
              ? { ...state.userProfile, is_pro: entitlement.isPro, is_ultra: !!entitlement.isUltra }
              : state.userProfile,
          }));
        },
        resetEntitlement: () => set({
          isProUser: false, isUltraUser: false, subscriptionInterval: 'month', subscriptionEndsAt: null,
          trialEndsAt: null, cancelAtPeriodEnd: false, tierOwnerId: null, entitlementFromServer: false,
        }),
        setCancelAtPeriodEnd: (cancelAtPeriodEnd) => set({ cancelAtPeriodEnd }),

        // Merchant
        currentStore: null,
        setCurrentStore: (currentStore) => set({ currentStore, lastSettingsUpdate: Date.now() }),
        selectStore: (store) => get().setCurrentStore(store),
        tripDateTime: new Date(),
        setTripDateTime: (tripDateTime) => set({ tripDateTime }),
        tripBudget: null,
        setTripBudget: (amount) => set({
          tripBudget: amount != null && amount > 0 ? Math.round(amount * 100) / 100 : null,
          budgetPromptDismissed: false,
          lastSettingsUpdate: Date.now(),
        }),
        budgetPromptDismissed: false,
        setBudgetPromptDismissed: (budgetPromptDismissed) => set({ budgetPromptDismissed, lastSettingsUpdate: Date.now() }),
        resetTripBudget: () => set({ tripBudget: null, budgetPromptDismissed: false, lastSettingsUpdate: Date.now() }),
        isSyncing: false,
        setIsSyncing: (isSyncing) => set({ isSyncing }),
        isTimeManual: false,
        setIsTimeManual: (isTimeManual) => set({ isTimeManual }),
        receiptImageUrl: null,
        setReceiptImageUrl: (receiptImageUrl) => set({ receiptImageUrl }),
        receiptSubtotal: null,
        setReceiptSubtotal: (receiptSubtotal) => set({ receiptSubtotal }),
        unitSystem: 'imperial',
        setUnitSystem: (unitSystem) => set({ unitSystem, lastSettingsUpdate: Date.now() }),
        massUnits: ['oz', 'lb'],
        setMassUnits: (massUnits) => set({ massUnits, lastSettingsUpdate: Date.now() }),
        zipCode: null,
        setZipCode: (zipCode) => set({ zipCode, lastSettingsUpdate: Date.now() }),
        language: i18n.language || 'en',
        // Language and currency are independent: a French speaker in Canada keeps CAD.
        setLanguage: (language) => {
          i18n.changeLanguage(language);
          syncUrlLanguage(language);
          set({
            language,
            lastSettingsUpdate: Date.now(),
            lastLanguageChangeAt: Date.now()
          });
        },
        lastLanguageChangeAt: 0,
        currency: (LANGUAGES.find(l => l.code === (i18n.language?.split('-')[0] || 'en'))?.defaultCurrency) || 'USD',
        setCurrency: (currency) => set({ currency, lastSettingsUpdate: Date.now() }),
        // History
        history: [],
        setHistory: (historyOrFn) => set((state) => {
          const nextHistory = typeof historyOrFn === 'function' ? historyOrFn(state.history) : historyOrFn;
          const seen = new Set();
          const uniqueHistory = nextHistory.filter(entry => {
            if (seen.has(entry.id)) return false;
            seen.add(entry.id);
            return true;
          });
          return {
            history: uniqueHistory,
            lastHistoryUpdate: Date.now()
          };
        }),
        lastHistoryUpdate: 0,
        lastSettingsUpdate: 0,
        lastTripSavedTimestamp: 0,
        latticeParticipation: true,
        setLatticeParticipation: (latticeParticipation) => {
          // The flag only; whoever changes it saves it (Settings, Bulk Import's donate
          // prompt), and the loaded profile copy is updated from the saved result.
          set({ latticeParticipation, lastSettingsUpdate: Date.now() });

          // Zustand's persist middleware does not update already-open tabs. Emit a
          // narrow storage event so Settings reflects a Bulk Import change instantly.
          if (typeof window !== 'undefined') {
            localStorage.setItem('cartlens-lattice-participation-sync', JSON.stringify({
              enabled: latticeParticipation,
              updatedAt: Date.now(),
            }));
          }
        },

        // Insights & Intelligence
        analytics: null,
        setAnalytics: (analytics) => set({ analytics }),
        dismissedSuggestions: [],
        dismissSuggestion: (name) => set((state) => ({ 
          dismissedSuggestions: [...state.dismissedSuggestions, name] 
        })),
        basketInsight: null,
        basketInsightProfileSignature: null,
        setBasketInsight: (basketInsight, profileSignature) => set((state) => ({
          basketInsight,
          basketInsightProfileSignature: profileSignature || null,
          lastInsightGeneratedTimestamp: basketInsight ? Date.now() : state.lastInsightGeneratedTimestamp,
          lastSettingsUpdate: Date.now()
        })),
        lastInsightGeneratedTimestamp: 0,
        setLastInsightGeneratedTimestamp: (lastInsightGeneratedTimestamp) => set({ lastInsightGeneratedTimestamp, lastSettingsUpdate: Date.now() }),
        lastBasketChangeTimestamp: 0,
        setLastBasketChangeTimestamp: (lastBasketChangeTimestamp) => set({ lastBasketChangeTimestamp, lastSettingsUpdate: Date.now() }),
        masterInsight: null,
        setMasterInsight: (masterInsight) => set({ masterInsight }),
        isInsightOnCooldown: false,
        cooldownTimeLeft: 0,
        handleBypassCooldown: (type) => set({ isInsightOnCooldown: false, cooldownTimeLeft: 0 }),

        nearbyStores: [],
        setNearbyStores: (nearbyStores) => set({ nearbyStores, lastSettingsUpdate: Date.now() }),

        chatPosition: null,
        // Synced like other device state (buildCloudSyncPayload compares it with the profile).
        setChatPosition: (chatPosition) => set({ chatPosition }),
        cookieConsent: UNDECIDED_CONSENT,
        // Saved to the profile by the background sync, which compares this choice with
        // the loaded profile (buildCloudSyncPayload), so the loaded copy is left alone here.
        setCookieConsent: ({ analytics, advertising }) => set({
          cookieConsent: {
            analytics,
            advertising,
            decidedAt: new Date().toISOString(),
            version: CONSENT_VERSION,
          },
          isConsentPanelOpen: false,
        }),
        isConsentPanelOpen: false,
        setConsentPanelOpen: (isConsentPanelOpen) => set({ isConsentPanelOpen }),
        // Strict until the policy loads, so nothing optional runs early.
        consentOptInRequired: true,
        setConsentOptInRequired: (consentOptInRequired) => set({ consentOptInRequired }),
        hasCompletedTour: false,
        setHasCompletedTour: (hasCompletedTour) => set({ hasCompletedTour, lastSettingsUpdate: Date.now() }),
        completedTours: [],
        setTourCompleted: (tourId) => set((state) => ({ 
          completedTours: state.completedTours.includes(tourId) ? state.completedTours : [...state.completedTours, tourId],
          lastSettingsUpdate: Date.now()
        })),
        resetTours: () => set({ completedTours: [], lastSettingsUpdate: Date.now() }),

        confirmations: {},
        setConfirmation: (nonce, status, threadId) => set((state) => ({
          confirmations: {
            ...state.confirmations,
            [nonce]: { status, threadId, timestamp: Date.now() }
          }
        })),
        clearConfirmations: (threadId) => set((state) => {
          if (!threadId) return { confirmations: {} };
          const next = { ...state.confirmations };
          Object.keys(next).forEach(nonce => {
            if (next[nonce].threadId === threadId) delete next[nonce];
          });
          return { confirmations: next };
        }),

        hydrated: false,
        setHydrated: (hydrated) => set({ hydrated }),
        isCinematicLoading: false,
        appLoadingProgress: 0,
        load: async (supabase, userId, force = false, clerkUser?: any) => {
          if (!supabase || !userId) return;
          if (force) set({ isCinematicLoading: true, appLoadingProgress: 10 });
          try {
            // 1. Initial Fetch
            let profile = await getProfile(supabase, userId);

            // 2. Just-in-Time Provisioning: Create profile if it doesn't exist
            if (!profile && clerkUser) {
              console.log('[Store] Profile missing. Provisioning new identity node...');
              const email = clerkUser.primaryEmailAddress?.emailAddress || clerkUser.emailAddresses[0]?.emailAddress || '';
              try {
                profile = await createProfile(
                  supabase, 
                  userId, 
                  email, 
                  clerkUser.firstName, 
                  clerkUser.lastName
                );
                console.log('[Store] Identity node provisioned successfully.');
              } catch (createErr) {
                console.error('[Store] Failed to provision identity node:', createErr);
              }
            }

            // 3. Fetch remaining data nodes
            const results = await Promise.allSettled([
              getCategories(supabase, userId),
              getHistory(supabase, userId, 20, 0),
              // Always fresh here; also seeds the query cache that chat tools read from.
              fetchSpendingAnalytics(supabase, userId, { fresh: true }),
              getMasterInsight(supabase, userId),
              getBasketInsight(supabase, userId)
            ]);
            
            const cats = results[0].status === 'fulfilled' ? results[0].value : null;
            const history = results[1].status === 'fulfilled' ? results[1].value : null;
            const analytics = results[2].status === 'fulfilled' ? results[2].value : null;
            const masterInsight = results[3].status === 'fulfilled' ? results[3].value : null;
            const basketInsight = results[4].status === 'fulfilled' ? results[4].value : null;
            
            console.log('[Store] Hydration Data:', {
              profile: !!profile,
              historyCount: history?.length || 0,
              masterInsight: !!masterInsight,
              basketTipsCount: basketInsight ? readSavedBasketTips(basketInsight.data).tips.length : 0
            });

            if (force) set({ appLoadingProgress: 60 });
            set((state) => {
              const nextState: Partial<AppState> = {};
              if (profile) {
                nextState.userProfile = profile;
                Object.assign(nextState, tierFromProfile(state, profile));

                nextState.currency = profile.settings?.interface?.currency || state.currency;

                // A `?lng=` in the URL, or a language picked in-app moments ago, always wins
                // over the saved profile language — otherwise it gets silently reverted by this
                // async profile load, since the in-app pick is only persisted to the profile via
                // a 2s-debounced auto-save (see `settingsSaveTimeoutRef` in App.tsx).
                const urlLng = typeof window !== 'undefined'
                  ? new URLSearchParams(window.location.search).get('lng')
                  : null;
                const recentLocalLanguageChange = state.lastLanguageChangeAt > 0
                  && (Date.now() - state.lastLanguageChangeAt) < 5000;
                if (urlLng || recentLocalLanguageChange) {
                  nextState.language = state.language;
                } else {
                  nextState.language = profile.settings?.interface?.language || state.language;

                  // CRITICAL: Update i18n instance with the loaded language
                  if (nextState.language && i18n.language !== nextState.language) {
                    i18n.changeLanguage(nextState.language);
                  }
                }
                if (profile.metadata?.unitSystem) nextState.unitSystem = profile.metadata.unitSystem;
                if (profile.metadata?.massUnits) nextState.massUnits = profile.metadata.massUnits;
                if (profile.metadata?.lastBasketChangeTimestamp) nextState.lastBasketChangeTimestamp = profile.metadata.lastBasketChangeTimestamp;
                if (profile.metadata?.lastInsightGeneratedTimestamp) nextState.lastInsightGeneratedTimestamp = profile.metadata.lastInsightGeneratedTimestamp;
                
                // Restore remaining metadata
                {
                  const remote = fromConsentSetting(profile.settings?.privacy?.cookie_consent);
                  if (remote && (!state.cookieConsent.decidedAt || remote.decidedAt! > state.cookieConsent.decidedAt)) {
                    nextState.cookieConsent = remote;
                  }
                }
                if (profile.metadata?.hasCompletedTour !== undefined) nextState.hasCompletedTour = profile.metadata.hasCompletedTour;
                if (profile.metadata?.completedTours) nextState.completedTours = profile.metadata.completedTours;
                if (profile.metadata?.currentStore) nextState.currentStore = profile.metadata.currentStore;
                if (profile.metadata?.tripBudget !== undefined) nextState.tripBudget = profile.metadata.tripBudget;
                if (profile.metadata?.budgetPromptDismissed !== undefined) nextState.budgetPromptDismissed = profile.metadata.budgetPromptDismissed;
                // This device's deliberate placement wins over a profile from another screen.
                if (!state.chatPosition && hasPositionViewport(profile.metadata?.chatPosition)) nextState.chatPosition = profile.metadata.chatPosition;
                if (profile.metadata?.nearbyStores) nextState.nearbyStores = profile.metadata.nearbyStores;
                if (profile.metadata?.isTimeManual !== undefined) nextState.isTimeManual = profile.metadata.isTimeManual;
                if (profile.metadata?.confirmations) nextState.confirmations = profile.metadata.confirmations;
                if (profile.settings?.privacy?.lattice_participation !== undefined) nextState.latticeParticipation = profile.settings.privacy.lattice_participation;
              }
              if (cats) nextState.categories = cats.length > 0 ? cats : DEFAULT_CATEGORIES;
              if (history) nextState.history = history;
               if (analytics) {
                const currentAnalytics = state.analytics;
                if (!currentAnalytics || (analytics.trip_count || 0) >= (currentAnalytics.trip_count || 0)) {
                  nextState.analytics = analytics;
                }
              }
              
              // Only update insights if we actually got something back to avoid wiping local persistence
              if (masterInsight) nextState.masterInsight = masterInsight;
              if (basketInsight) {
                const saved = readSavedBasketTips(basketInsight.data, basketInsight.generated_at);
                nextState.basketInsight = saved.tips;
                nextState.basketInsightProfileSignature = saved.profileSignature;
                nextState.lastInsightGeneratedTimestamp = saved.generatedAt;
              } else {
                nextState.basketInsight = null;
                nextState.basketInsightProfileSignature = null;
              }
              
              // USE SERVER-SIDE TIMESTAMPS FOR STALENESS LOGIC
              // This prevents everything from being "stale" on refresh because Date.now() would always be > insight.generatedAt
              if (profile) {
                const historyTs = profile.last_history_change ? new Date(profile.last_history_change).getTime() : 0;
                const settingsTs = profile.updated_at ? new Date(profile.updated_at).getTime() : 0;
                const insightTs = profile.last_insight_at ? new Date(profile.last_insight_at).getTime() : 0;
                
                // If history was updated but then an insight was generated, the history isn't "stale" relative to the insight
                // We normalize them so that if insight is newer than history, history update timestamp is at most equal to insight
                nextState.lastHistoryUpdate = historyTs;
                nextState.lastSettingsUpdate = settingsTs;
              } else {
                nextState.lastHistoryUpdate = 0;
                nextState.lastSettingsUpdate = 0;
              }
              
              return nextState;
            });
            if (force) set({ appLoadingProgress: 100 });
          } catch (err) {
            console.error("Store: Critical Load error:", err);
          } finally {
            if (force) {
              setTimeout(() => {
                try {
                  set({ isCinematicLoading: false, appLoadingProgress: 0 });
                } catch (e) {
                  console.error("Store: Finalize load error:", e);
                }
              }, 500);
            }
          }
        },
        syncLocalSettings: async () => {
          set({ lastSettingsUpdate: Date.now() });
        },

        // PWA
        pwaInstalled: false,
        setPwaInstalled: (pwaInstalled) => set({ pwaInstalled }),
        pwaInstallDismissed: null,
        setPwaInstallDismissed: (pwaInstallDismissed) => set({ pwaInstallDismissed }),

        // UI Actions Bridge
        uiActions: {},
        registerAction: (name, cb) => set((s) => ({ uiActions: { ...s.uiActions, [name]: cb } })),
        unregisterAction: (name) => set((s) => {
          const next = { ...s.uiActions };
          delete next[name];
          return { uiActions: next };
        }),
        triggerAction: (name, data) => {
          const action = get().uiActions[name];
          if (action) action(data);
        },

        pendingProfilePatch: null,
        setPendingProfilePatch: (pendingProfilePatch) => set({ pendingProfilePatch }),
        markProfilePatched: (patch) => set((state) => ({
          userProfile: state.userProfile ? mergeProfilePatch(state.userProfile, patch) : state.userProfile,
        })),
        // Plan, credits and storage from the server, without a full load(). The
        // user-editable parts (settings, ai_profile, metadata) stay as they are
        // here: a read that started before a save would otherwise put old values back.
        refreshAccount: async (supabase, userId) => {
          const fresh = await getProfile(supabase, userId);
          if (!fresh) return;
          set((state) => {
            if (!state.userProfile || state.userProfile.id !== fresh.id) return {};
            const { settings, ai_profile, metadata } = state.userProfile;
            return {
              userProfile: { ...fresh, settings, ai_profile, metadata },
              ...tierFromProfile(state, fresh),
            };
          });
        },
        syncStateToCloud: async (supabase, userId) => {
          const payload = buildCloudSyncPayload(get());
          if (!payload) return; // profile not loaded yet, or nothing changed
          try {
            await updateProfile(supabase, userId, payload as any);
            get().markProfilePatched(payload);
          } catch (e) {
            console.error("Failed to sync store metadata:", e);
          }
        },
        spendCredits: async (amount = 1) => {
          const state = get();
          if (!state.userProfile?.id || !state.hydrated) return false;
          try {
            const supabase = (window as any).supabase;
            if (!supabase) return false;
            const success = await spendCreditsRpc(supabase, state.userProfile.id, amount);
            if (success && state.userProfile) {
              set((s) => ({
                userProfile: {
                  ...s.userProfile!,
                  credits: Math.max(0, (s.userProfile?.credits || 0) - amount)
                }
              }));
            }
            return success;
          } catch (e) {
            console.error("Store: spendCredits failed:", e);
            return false;
          }
        }
      }),
      {
        name: 'cartlens-storage',
        // v1: the acknowledge-only privacyAccepted flag became category consent.
        // An old "Acknowledge" click was not a choice, so those visitors are asked again.
        version: 1,
        migrate: (persisted: any) => {
          if (persisted && typeof persisted === 'object') delete persisted.privacyAccepted;
          return persisted;
        },
        storage: createJSONStorage(() => ({
          getItem: (name) => localStorage.getItem(name),
          setItem: (name, value) => {
            try {
              localStorage.setItem(name, value);
            } catch (e) {
              console.warn("Zustand storage exceeded.");
            }
          },
          removeItem: (name) => localStorage.removeItem(name)
        })),
        onRehydrateStorage: () => (state) => {
          if (state) {
            // Purge stale lattice deal cache from localStorage — partialize nulls betterDeal
            // on write but cannot retroactively clean values written by older app versions.
            if (state.items) {
              state.items = state.items.map((item: any) => ({ ...item, betterDeal: null, latticeRecord: null, latticeReports: null, priceCheck: null }));
            }
            if (state.latticeItems) {
              state.latticeItems = state.latticeItems.map((item: any) => ({ ...item, betterDeal: null, latticeRecord: null, latticeReports: null, priceCheck: null }));
            }
            if ((state as any).isTimeManual && (state as any).tripDateTime) {
              (state as any).tripDateTime = new Date((state as any).tripDateTime);
            } else {
              (state as any).tripDateTime = new Date();
            }
            state.setHydrated(true);
          }
        },
        partialize: (state): any => ({
          items: state.items.map(item => ({
            ...item,
            imageUrl: item.imageUrl?.startsWith('data:') ? undefined : item.imageUrl,
            betterDeal: null,
            priceCheck: null
          })),
          latticeItems: state.latticeItems.map(item => ({
            ...item,
            imageUrl: item.imageUrl?.startsWith('data:') ? undefined : item.imageUrl,
            betterDeal: null,
            priceCheck: null
          })),
          seenBulkImportBatchIds: state.seenBulkImportBatchIds,
          operatingContext: state.operatingContext,
          currentStore: state.currentStore,
          // Keep the checkout photo alongside the draft basket across refreshes.
          receiptImageUrl: state.receiptImageUrl?.startsWith('blob:') || state.receiptImageUrl?.startsWith('data:') ? null : state.receiptImageUrl,
          tripBudget: state.tripBudget,
          budgetPromptDismissed: state.budgetPromptDismissed,
          isTimeManual: state.isTimeManual,
          // new Date(...): a version migration saves before onRehydrateStorage turns
          // the stored string back into a Date, and .toISOString() on the string
          // threw and blanked the app for returning visitors with a manual trip time.
          tripDateTime: state.isTimeManual && state.tripDateTime && !isNaN(new Date(state.tripDateTime).getTime())
            ? new Date(state.tripDateTime).toISOString()
            : undefined,
          unitSystem: state.unitSystem,
          massUnits: state.massUnits,
          zipCode: state.zipCode,
          language: state.language,
          currency: state.currency,
          cookieConsent: state.cookieConsent,
          hasCompletedTour: state.hasCompletedTour,
          completedTours: state.completedTours,
          chatPosition: state.chatPosition,
          nearbyStores: state.nearbyStores,
          lastBasketChangeTimestamp: state.lastBasketChangeTimestamp,
          pwaInstalled: state.pwaInstalled,
          pwaInstallDismissed: state.pwaInstallDismissed,
          // Last known plan, so a paid user doesn't see the Free UI while the app opens.
          // App.tsx drops it when a different account signs in.
          isProUser: state.isProUser,
          isUltraUser: state.isUltraUser,
          subscriptionInterval: state.subscriptionInterval,
          tierOwnerId: state.tierOwnerId,
        }),
      }
    )
  )
);

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== 'cartlens-lattice-participation-sync' || !event.newValue) return;

    try {
      const { enabled } = JSON.parse(event.newValue) as { enabled?: unknown };
      if (typeof enabled !== 'boolean') return;

      // Apply the remote tab's value without calling the public setter, which would
      // rebroadcast the same event back and create a cross-tab loop.
      useStore.setState((state) => ({
        latticeParticipation: enabled,
        userProfile: state.userProfile ? {
          ...state.userProfile,
          settings: {
            ...state.userProfile.settings,
            privacy: {
              ...(state.userProfile.settings?.privacy || {}),
              lattice_participation: enabled,
            },
          },
        } : state.userProfile,
        lastSettingsUpdate: Date.now(),
      }));
    } catch {
      // Ignore malformed values from older/local development builds.
    }
  });
}

useStore.subscribe(state => state.items, (items) => { liveState.items = items; });
useStore.subscribe(state => state.categories, (cats) => { liveState.categories = cats; });
useStore.subscribe(state => state.history, (hist) => { liveState.history = hist; });
useStore.subscribe(state => state.userProfile, (prof) => { liveState.profile = prof; });
useStore.subscribe(state => state.uiActions, (actions) => { 
  liveState.triggerAction = (name, data) => {
    const action = actions[name];
    if (action) action(data);
  };
});

// Wire direct setters — always delegate to the live Zustand instance at call time
liveState.setCurrentStore = (store) => useStore.getState().setCurrentStore(store);
liveState.setTripDateTime = (date) => useStore.getState().setTripDateTime(date);
liveState.setIsTimeManual = (v) => useStore.getState().setIsTimeManual(v);
liveState.setReceiptImageUrl = (url) => useStore.getState().setReceiptImageUrl(url);
liveState.setReceiptSubtotal = (subtotal) => useStore.getState().setReceiptSubtotal(subtotal);

const initialState = useStore.getState();
liveState.items = initialState.items;
liveState.categories = initialState.categories;
liveState.history = initialState.history;
liveState.profile = initialState.userProfile;
liveState.triggerAction = (name, data) => {
  const action = useStore.getState().uiActions[name];
  if (action) action(data);
};
